import type {
  CallSection,
  CallSectionId,
} from "@repo/app-types/entities/liveCoaching";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";

export interface ProgressWidgetProps {
  sections: readonly CallSection[];
}

export const ProgressWidget: FC<ProgressWidgetProps> = (props) => {
  const [expandedSectionIds, setExpandedSectionIds] = useState<
    ReadonlySet<CallSectionId>
  >(new Set());

  const onSectionExpandedToggle = (sectionId: CallSectionId) => {
    setExpandedSectionIds((prev) => {
      const next = new Set(prev);

      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }

      return next;
    });
  };

  if (props.sections.length === 0) {
    return null;
  }

  // TODO: Use proper semantic elements (disclosure for accordion, bulleted list for bullets)
  return (
    <div className="mb-3">
      <p className="mb-2 text-[10px] font-semibold text-slate-400">
        Call progress
      </p>
      <div className="relative pl-4">
        <div className="absolute top-1 bottom-1 left-[11px] w-px bg-slate-200" />
        <div className="space-y-1">
          {props.sections.map((section) => {
            const isExpanded = expandedSectionIds.has(section.id);

            return (
              <div key={section.id}>
                <button
                  onClick={() => onSectionExpandedToggle(section.id)}
                  className="relative flex w-full items-center gap-2.5 py-1 text-left"
                >
                  <div className="relative z-10 -ml-4 flex size-[22px] shrink-0 items-center justify-center">
                    {section.status === "completed" ? (
                      <div className="flex size-[18px] items-center justify-center rounded-full bg-emerald-500">
                        <IconCheck className="size-3 text-white" />
                      </div>
                    ) : section.status === "in_progress" ? (
                      <div className="flex size-[18px] items-center justify-center rounded-full border-2 border-indigo-500 bg-white">
                        <div className="size-2 rounded-full bg-indigo-500" />
                      </div>
                    ) : (
                      <div className="size-[18px] rounded-full border-2 border-border-strong bg-white" />
                    )}
                  </div>
                  <span
                    className={twMerge(
                      "flex-1 text-xs",
                      section.status === "in_progress"
                        ? "font-semibold text-foreground"
                        : section.status === "completed"
                          ? "text-muted-foreground"
                          : "text-slate-400",
                    )}
                  >
                    {section.title}
                  </span>
                  {section.subsections.length > 0 && (
                    <IconChevronDown
                      className={twMerge(
                        "size-3 shrink-0 text-slate-400 transition-transform",
                        isExpanded && "rotate-180",
                      )}
                    />
                  )}
                </button>
                {isExpanded && (
                  <div className="ml-5 space-y-0.5 pt-0.5 pb-1">
                    {section.subsections.map((sub, index) => (
                      <p
                        key={index}
                        className="text-[11px] leading-relaxed text-slate-400"
                      >
                        • {sub.title}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
